<script setup lang="ts">
import {
  MedicalSystemCatalogDto,
  MedicalSystemRtSystemDto,
  instanceOfBeamConstancyWorkItemDto,
  instanceOfManualTaskWorkItemDto,
} from '@/api/generator';
import { I18nKey } from '@/utils/typings';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import JobExecutionBCCForm from './JobExecutionBCCForm.vue';
import JobExecutionManualForm from './JobExecutionManualForm.vue';
import RTSystemTile from '@/components/RTSystemTile/RTSystemTile.vue';
import JobExecutionProgress from './JobExecutionProgress.vue';
import { summarizeWorkItemProgress } from '../utils/jobExecutionProgress';
import { useJobOccurrence } from '../utils/useJobOccurrence';
import { ref, watch } from 'vue';
import { useFormMode } from '@/components/forms/useFormMode';
import { FormMode } from '@/components/forms/types';
import { useExpandAllPanels } from '../utils/useExpandAllPanels';
import { mdiPlus, mdiChevronDown, mdiChevronUp } from '@mdi/js';
import CommentInlineDialog from '@/components/comments/CommentInlineDialog.vue';
import CommentCount from '@/components/forms/CommentCount/CommentCount.vue';
import InternalLink from '@/components/InternalLink/InternalLink.vue';
import { routenames } from '@/router/routenames';
import { watchEffect } from 'vue';
import { computedAsync } from '@vueuse/core';
import useCrudForComments from '@/components/comments/useCrudForComments';
import { info } from '@/components/icons/icons';
import EntityChange from '@/components/EntityHistory/EntityChange.vue';
import { getEntityChangeEvent } from '@/components/EntityHistory/entityChange';

const props = defineProps<{
  targetSystem?: MedicalSystemRtSystemDto | MedicalSystemCatalogDto | null;
  expand?: boolean;
}>();

const mode = useFormMode();

const { workitems, reload, jobOccurrence, inAnyStateOf } = useJobOccurrence();

const { getCommentsForEnity } = useCrudForComments(jobOccurrence.value.id, 'JobOccurrence');

const { expandedIndices, expandAll, collapseAll, anyExpanded, allExpanded } = useExpandAllPanels(
  workitems.value ?? undefined,
);

function autoExpand(formMode: FormMode) {
  const isTaskPresent = !!workitems.value?.length;

  const isInExecution = formMode === 'execution';

  if (isInExecution && !anyExpanded.value && isTaskPresent && props.expand) {
    expandedIndices.value = [0];
  }
}

function expandSingleTask() {
  if (workitems.value?.length === 1) {
    expandedIndices.value = [0];
  }
}

const openCommentsDialogId = ref<string | undefined>(undefined);

function toggleCommentsDialog(open?: boolean, id?: string) {
  openCommentsDialogId.value = open ? id : undefined;
}

const comment = computedAsync(async () => {
  if (inAnyStateOf(['ReworkRequested', 'InExecution', 'Paused'])) {
    const comments = await getCommentsForEnity(['ReworkRequested']);
    return comments?.at(-1);
  }
});

watch(() => mode.value, autoExpand);

watchEffect(expandSingleTask);
</script>

<template>
  <v-row v-if="targetSystem">
    <v-col>
      <RTSystemTile :item="{ ...targetSystem, serialNumber: undefined }" class="fit-content" />
    </v-col>

    <v-col>
      <v-alert v-if="comment" type="info" :icon="info" density="compact" class="mb-4">
        <template #title>
          <div>
            <div>
              {{ $t('StateOfUseEnum.ReworkRequested') }}
            </div>

            <EntityChange
              :entityChange="getEntityChangeEvent(comment, 'created')"
              :singleLine="true"
              class="font-weight-regular text-body-1"
            />
          </div>
        </template>

        <template #text>
          <div class="mt-4">
            {{ comment.description }}
          </div>
        </template>
      </v-alert>
    </v-col>
  </v-row>

  <div v-if="workitems?.length">
    <div class="d-flex w-100 justify-end mb-2">
      <TextVBtn
        :text="$t('ExpandAll')"
        :prependIcon="mdiChevronDown"
        size="x-small"
        :disabled="allExpanded"
        class="text-caption"
        @click="expandAll"
      />

      <TextVBtn
        :text="$t('CollapseAll')"
        :prependIcon="mdiChevronUp"
        size="x-small"
        :disabled="!anyExpanded"
        class="text-caption"
        @click="collapseAll"
      />
    </div>

    <v-expansion-panels v-model="expandedIndices" multiple>
      <v-expansion-panel
        v-for="workitem in workitems"
        :key="workitem.taskInJobId"
        :title="workitem.name || undefined"
        :text="workitem.description || undefined"
        class="workitems"
      >
        <template #title>
          <div class="d-flex w-100 align-center py-2">
            <div class="me-auto d-flex align-center">
              <IdentifierLabel
                :label="workitem.name"
                :subline="$t(`TaskTypeDescriptionEnum.${workitem.taskType}` as I18nKey)"
                class="text-h5"
              >
                <template #suffix>
                  <InternalLink
                    v-if="workitem.taskId"
                    :permission="['Global_TaskManagement_ViewDetail']"
                    :tooltip="$t('GetToTaskItem')"
                    :to="{
                      name: routenames.tasks.details,
                      params: {
                        id: workitem.taskId,
                      },
                    }"
                  />
                </template>
              </IdentifierLabel>
            </div>

            <div class="mr-3">
              <CommentCount
                :value="workitem"
                @click.stop="toggleCommentsDialog(true, workitem.id)"
              />
            </div>

            <JobExecutionProgress :value="summarizeWorkItemProgress(workitem)" class="mr-3" />
          </div>
        </template>

        <CommentInlineDialog
          :entity="workitem"
          entityWithCommentType="WorkItem"
          :open="openCommentsDialogId === workitem.id"
          @update:open="toggleCommentsDialog($event, workitem.id)"
          @change="reload()"
        >
          <div />
        </CommentInlineDialog>

        <template #text>
          <div class="d-flex justify-end">
            <TextVBtn
              :prependIcon="mdiPlus"
              :text="$t('AddComment')"
              size="x-small"
              @click="toggleCommentsDialog(true, workitem.id)"
            />
          </div>

          <JobExecutionBCCForm
            v-if="instanceOfBeamConstancyWorkItemDto(workitem)"
            :workitem="workitem"
          />

          <JobExecutionManualForm
            v-if="instanceOfManualTaskWorkItemDto(workitem)"
            :workitem="workitem"
          />
        </template>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>

  <!-- No work planned for this job -->
  <template v-if="!targetSystem || !workitems?.length">
    <p class="gutterTop text-body-1 font-weight-bold text-primary-lighten-2 ml-4">
      {{ $t('NoWorkPlannedForThisJobMsg') }}
    </p>
  </template>
</template>
